import { useEffect, type RefObject } from 'react';

/**
 * Replays the fill-highlight pulse on a row whenever it (re-)becomes the
 * assistant's highlight target — WITHOUT remounting it (which would drop focus /
 * in-progress typing in the row's inputs). Scrolls the row into view and
 * restarts the CSS animation via a reflow. Shared by the config and filter rows
 * so the imperative replay lives in one place.
 *
 * @param ref - The row element that carries the `trigger-fill-highlight` class
 * @param highlighted - Whether this row is the current highlight target
 * @param nonce - Monotonic nonce from {@link useFillHighlight}; bumping it
 *   replays even when `highlighted` is unchanged (same field/condition refilled)
 */
export function useHighlightReplay<T extends HTMLElement>(
  ref: RefObject<T | null>,
  highlighted: boolean,
  nonce: number | undefined,
): void {
  // eslint-disable-next-line no-restricted-syntax -- reason: imperatively replay the highlight animation + scroll into view
  useEffect(() => {
    const el = ref.current;
    if (!el || !highlighted) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    el.style.animation = 'none';
    void el.offsetWidth; // force reflow so the CSS animation restarts
    el.style.animation = '';
  }, [ref, highlighted, nonce]);
}
